/* eslint-disable import/prefer-default-export */

import _ from 'lodash';
import React from 'react';
import {
    createPortal
} from 'react-dom';

import PropTypes from 'prop-types';

import {
    usePopperTooltip
} from 'react-popper-tooltip';

import {
    useIsExporting
} from '../hooks/useExport.js';

RIQPopover.propTypes = {
    additionalArrowProps: PropTypes.object,
    additionalHookProps: PropTypes.object,
    additionalTooltipProps: PropTypes.object,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    placement: PropTypes.string,
    popoverNode: PropTypes.node,
    triggerNode: PropTypes.node.isRequired
};

RIQPopover.StandardDelayMS = 700;

export function RIQPopover({children, className, containerClassName, additionalArrowProps, additionalHookProps, additionalTooltipProps, placement, portalNode, triggerNode, variant = 'default'}) {

    const hookProps = _.extend({
        placement: placement || 'right',
        delayHide: 200, // 200ms delay before hiding the tooltip
        interactive: true, // This allows selecting text/clicking links in the tooltip
        trigger: ['hover', 'focus'],
        offset: [0, 8]
    }, additionalHookProps);
    const {getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible} = usePopperTooltip(hookProps);

    const variantProperties = {};
    switch (variant) {
        case 'default':
            variantProperties.className = 'riq-popover-container tlw-text-gray-800 tlw-bg-white tlw-text-md tlw-border-gray-600 tlw-border-solid tlw-border tlw-rounded-lg';
            variantProperties.arrow = {
                className: 'riq-popover-arrow'
            };
            variantProperties.children = children;
            break;

        case 'tooltip':
            variantProperties.className = 'riq-tooltip-container tlw-text-white tlw-bg-black tlw-text-md tlw-border-black tlw-border-solid tlw-border tlw-rounded-sm';
            variantProperties.arrow = {
                className: 'riq-tooltip-arrow'
            };
            variantProperties.children = (
                <div className='tlw-px-4 tlw-py-1 tlw-text-sm tlw-leading-base'>{children}</div>
            );
            break;

        default:
            throw new Error(`Unknown popover variant: ${variant}`);
    }

    const popover = (
        <div
            ref={setTooltipRef}
            {...getTooltipProps({
                className: `${variantProperties.className} ${containerClassName}`,
                // eslint-disable-next-line object-curly-newline
                style: {textShadow: 'none'} // tailwind doesn't support text-shadow
            })}
            {...additionalTooltipProps}
        >
            <div {...getArrowProps({
                className: variantProperties.arrow.className
            })}
            {...additionalArrowProps} />

            {variantProperties.children}
        </div>
    );

    return (
        <>
            <div
                role='button'
                tabIndex='0'
                className={`tlw-inline-block tlw-border-0 tlw-bg-transparent tlw-p-0 ${className}`}
                ref={setTriggerRef}>
                {triggerNode}
            </div>
            {visible && (portalNode ? createPortal(popover, portalNode) : popover)}
        </>
    );
}

RIQInfoPopover.propTypes = {
    title: PropTypes.string,
    description: PropTypes.node.isRequired,
    isDescriptionHtml: PropTypes.bool
};

export function RIQInfoPopover({description, isDescriptionHtml, portalNode, title}) {
    if (useIsExporting()) {
        return;
    }

    return (
        <RIQPopover
            className='tlw-text-gray-500'
            portalNode={portalNode}
            triggerNode={<span className='fa fa-info-circle fa-color fa-fw tlw-cursor-pointer' />}
        >
            {title && <h3
                className='tlw-m-0 tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-900'>
                {title}
            </h3>}
            {isDescriptionHtml &&
            <div
                className='tlw-px-7 tlw-py-4.5 tlw-font-normal tlw-normal-case tlw-leading-base tlw-text-gray-900'
                dangerouslySetInnerHTML={{
                    __html: description
                }}>
            </div>}
            {!isDescriptionHtml &&
            <div
                className='tlw-px-7 tlw-py-4.5 tlw-font-normal tlw-normal-case tlw-leading-base tlw-text-gray-900'>
                {description}
            </div>}
        </RIQPopover>
    );
}

export function useRIQTooltip({text, placement, disabled, delayHide, delayShow}) {
    const {getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible} = usePopperTooltip({
        placement: placement || 'right',
        delayHide: _.isUndefined(delayHide) ? 200 : delayHide,
        delayShow: _.isUndefined(delayShow) ? 0 : delayShow,
        interactive: true, // This allows selecting text/clicking links in the tooltip
        trigger: ['hover']
    });

    const tooltipNode = (
        <>
            {(visible && !disabled) && (
                <div
                    ref={setTooltipRef}
                    {...getTooltipProps({
                        className: 'riq-tooltip-container tlw-text-white tlw-bg-black tlw-text-md tlw-border-black tlw-border-solid tlw-border tlw-rounded-sm',
                        // eslint-disable-next-line object-curly-newline
                        style: {textShadow: 'none'} // tailwind doesn't support text-shadow
                    })}
                >
                    <div {...getArrowProps({
                        className: 'riq-tooltip-arrow'
                    })} />

                    <div
                        className='tlw-px-4 tlw-py-1 tlw-text-sm tlw-leading-base'>
                        {text}
                    </div>
                </div>
            )}
        </>
    );

    return {
        setTriggerRef,
        tooltipNode
    };
}
